import { Component, Input, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { DigitalMemoryLogService } from "../../services/digital-memory-log.service";
import { ToastrService } from "ngx-toastr";
import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { CTableConfig } from "src/app/core/ui/c-table/c-table-config";

interface IEmailLog {
    email: string,
    firstName: string,
    lastName: string,
    digitalMemoryLogId: number,
    createdAt: string,
    reSendAt: string,
    updatedAt: string,
    createdBy: string,
    updatedBy: string,
    id: number
}
@Component({
  selector: "app-report-popup",
  templateUrl: "./report-popup.component.html",
  styleUrls: ["./report-popup.component.scss"]
})
export class ReportPopupComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() digitalLogId: number;
  @Input() digitalLogName: string;
  @ViewChild('searchInput') searchInput: ElementRef;
  @ViewChild('cTable') cTable: any;

  emailLogs: IEmailLog[] = [];
  filteredEmailLogs: IEmailLog[] = [];
  loading = false;
  selectedTimeFilter = "all";
  searchValue: string = "";
  searchChanged: Subject<string> = new Subject<string>();
  private searchSubscription: Subscription;
  hasSearched = false; 
  totalSent: number = 0;
  private isUpdatingData = false;
  timeFilters = [
    { value: "all", label: "All Time" },
    { value: "24h", label: "Last 24h" },
    { value: "week", label: "This Week" },
    { value: "month", label: "This Month" },
    { value: "lastMonth", label: "Last Month" }
  ];

  // C-Table configuration
  tableOptions:CTableConfig = {
    data: [],
    useLocalTable: false, 
    usePaging: true,
    isAutoload: false,
    showTableFooter: true,
    extraData: {
      hasSearched: false,
      searchValue: ""
    },
    pagingOptions: {
      showTotalRecords: true,
      pageSizes: [ 10, 20, 30],
      pageSize: 10,
      currentPage: 1,
    },
    tableContent: {
      emptyData: "No email logs found",
      emptyDataSearch: "No email logs found for \"{{searchValue}}\""
    },
    refresh: (searchModel: any, tableInstance: any) => {      
      // Prevent API call if already updating
      if (this.isUpdatingData) {
        return;
      }
      
      // Extract pagination info from searchModel
      const currentPage = searchModel._page || 1;
      const pageSize = searchModel._pageSize || 10;
      const skip = (currentPage - 1) * pageSize;       
       // Update table options to sync with current values
       this.tableOptions.pagingOptions.currentPage = currentPage;
       this.tableOptions.pagingOptions.pageSize = pageSize;
       
       // Load data with pagination
       this.loadEmailLogs(skip, pageSize);
    },
    columnDefs: [
      {
        displayName: 'Email',
        field: 'email',
        cellClass: 'text-inline',
        cellHeaderClass: 'text-inline',
      },
      {
        displayName: 'First Name',
        field: 'firstName',
        cellClass: 'text-inline',
        cellHeaderClass: 'text-inline',
      },
      {
        displayName: 'Last Name',
        field: 'lastName',
        cellClass: 'text-inline',
        cellHeaderClass: 'text-inline',
      },
      {
        displayName: 'Date Sent',
        field: 'createdAt',
        cellPipe: 'cDateTime',
        cellClass: 'text-inline',
        cellHeaderClass: 'text-inline',
      }
    ]
  };

  constructor(
    public activeModal: NgbActiveModal,
    private digitalLogService: DigitalMemoryLogService,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    // Initialize data arrays
    this.emailLogs = [];
    this.filteredEmailLogs = [];
    
    // Setup search debounce
    this.searchSubscription = this.searchChanged
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((searchText) => {
        this.searchValue = searchText;
        // Reset to first page when searching
        this.tableOptions.pagingOptions.currentPage = 1;
        this.loadEmailLogs(0, this.tableOptions.pagingOptions.pageSize);
      });

    // Initialize extraData first
    this.tableOptions.extraData.hasSearched = this.hasSearched;
    this.tableOptions.extraData.searchValue = this.searchValue;
    
    // Initialize table data
    this.tableOptions.data = this.filteredEmailLogs;
  }

  ngAfterViewInit() {
    // Initialize c-table first, then load data
    if (this.cTable) {
      this.cTable.setData([], 0);
      // Load email logs with initial pagination after c-table is ready
      this.loadEmailLogs(0, this.tableOptions.pagingOptions.pageSize);
    }
  }

  ngOnDestroy() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  loadEmailLogs(skip = 0, limit = 10) {
    // Prevent multiple API calls
    if (this.loading) {
      return;
    }
    
    // Store current focus element
    const activeElement = document.activeElement;
    
    // Update paging options to sync with current values
    this.tableOptions.pagingOptions.pageSize = limit;
    
    this.loading = true;
    this.isUpdatingData = true;
    
    // Get date range for API call
    const { fromDate, toDate } = this.getDateRange();
    
    // Format dates for API (ISO string format)
    const formattedFromDate = fromDate ? fromDate.toISOString() : null;
    const formattedToDate = toDate ? toDate.toISOString() : null;
    
    this.digitalLogService.getEmailLogs( this.searchValue, formattedFromDate, formattedToDate, this.digitalLogId, skip, limit).subscribe(
      (res: any) => {        
        if (res && res.data) {
          this.emailLogs = res.data || [];
          this.totalSent = res.total || 0;
        } else {
          this.emailLogs = res || [];
          this.totalSent = this.emailLogs.length;
        }

        // Update table data
        this.applyFilter();
        
        // Update extraData for c-table
        this.tableOptions.extraData.hasSearched = this.hasSearched;
        this.tableOptions.extraData.searchValue = this.searchValue;
        
        // Update tableContent for proper empty state messages
        this.tableOptions.tableContent.emptyDataSearch = `No email logs found for "${this.searchValue}"`;
        
        // Update c-table directly without setTimeout
        if (this.cTable) {
           this.cTable.setData(this.filteredEmailLogs, this.totalSent);
           // Update c-table's current page to match the actual page we're on
           const currentPage = Math.floor(skip / limit) + 1;
           this.cTable._page = currentPage;
           this.cTable.options.pagingOptions.currentPage = currentPage;
           // Update isSearching flag in c-table
           this.cTable.isSearching = this.hasSearched && this.searchValue.trim() !== '';
           // Update tableContent in c-table
           this.cTable.tableContent = this.tableOptions.tableContent;
        }
        
        this.loading = false;
        this.isUpdatingData = false;
        
        // Restore focus after data update
        setTimeout(() => {
          if (activeElement && activeElement instanceof HTMLElement) {
            activeElement.focus();
          }
        }, 0);
      },
      (error) => {
        this.toastr.error("Failed to load email logs", "Error");
        this.loading = false;
        this.isUpdatingData = false;
        
        // Restore focus even on error
        setTimeout(() => {
          if (activeElement && activeElement instanceof HTMLElement) {
            activeElement.focus();
          }
        }, 0);
      }
    );
  }

  getDateRange() {
    const now = new Date();
    let fromDate: Date;
    let toDate: Date = now;

    switch (this.selectedTimeFilter) {
      case "24h":
        fromDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case "week":        
        fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay() + 1);
        break;
      case "month":
        fromDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case "lastMonth":
        fromDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        toDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      default:
        fromDate = undefined;
        toDate = undefined;
    }

    return { fromDate, toDate };
  }
  onTimeFilterChange() {
    this.hasSearched = false; // Reset search flag when time filter changes
    // Reset to first page when filter changes
    this.tableOptions.pagingOptions.currentPage = 1;
    this.loadEmailLogs(0, this.tableOptions.pagingOptions.pageSize);
    
    // Update extraData for c-table
    this.tableOptions.extraData.hasSearched = this.hasSearched;
    this.tableOptions.extraData.searchValue = this.searchValue;
  }

  onSearchInputChange(event: any) {
    let search = event.target.value;
    // Only mark as searched if there's actually a search term
    this.hasSearched = search.trim().length > 0;
    this.searchChanged.next(search);
    
    // Update extraData for c-table without triggering re-render
    this.tableOptions.extraData.hasSearched = this.hasSearched;
    this.tableOptions.extraData.searchValue = search;
    
    // Update tableContent for proper empty state messages
    this.tableOptions.tableContent.emptyDataSearch = `No email logs found for "${search}"`;
  }

  onSearch() {
    this.hasSearched = true; // Mark that user has searched
    // Reset to first page when searching
    this.tableOptions.pagingOptions.currentPage = 1;
    this.loadEmailLogs(0, this.tableOptions.pagingOptions.pageSize);
    
    // Update extraData for c-table
    this.tableOptions.extraData.hasSearched = this.hasSearched;
    this.tableOptions.extraData.searchValue = this.searchValue;
    
    // Update tableContent for proper empty state messages
    this.tableOptions.tableContent.emptyDataSearch = `No email logs found for "${this.searchValue}"`;
  }

  applyFilter() {
    // Since API now handles filtering, just update the data
    this.filteredEmailLogs = this.emailLogs;
    this.tableOptions.data = this.filteredEmailLogs;
    // Update c-table data if component is available and not updating
    if (this.cTable && !this.isUpdatingData) {
      this.cTable.setData(this.filteredEmailLogs, this.totalSent);
      // Update isSearching flag in c-table
      this.cTable.isSearching = this.hasSearched && this.searchValue.trim() !== '';
      // Update tableContent in c-table
      this.cTable.tableContent = this.tableOptions.tableContent;
    }
  }

  // Track by function to prevent unnecessary re-renders
  trackByEmail(index: number, item: any): any {
    return item.id || index;
  }

  close() {
    this.activeModal.dismiss();
  }



}
